import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { triggerPreviewGeneration } from "@/api/previews.api";
import { listActiveCategories } from "@/api/categories.api";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ImageCarousel } from "@/components/common/ImageCarousel";
import { useJobPolling } from "@/hooks/useJobPolling";
import { useMe } from "@/hooks/useMe";
import { useTheme } from "@/hooks/useTheme";
import { JobProgressPanel } from "@/pages/catalog/components/JobProgressPanel";
import type { CategorySummary } from "@/types/catalog";
import type { ThemeDetailResponse } from "@/types/themes";

import { ThemeMetadataStrip } from "./components/ThemeMetadataStrip";

import "./ThemeDetailPage.css";

// Mirrors hub_api.api.theme_routes.PREVIEW_SCREEN_KEYS. Sourced as a
// constant so a future addition (e.g. `about`) only needs editing here.
const FEATURE_KEY = "feature_graphic" as const;
const SCREEN_KEYS = [
  "home_dashboard",
  "registration",
  "class_schedule",
] as const;

interface CarouselImage {
  readonly url: string;
  readonly label: string;
  readonly key?: string;
}

interface CarouselBuildResult {
  readonly images: CarouselImage[];
  readonly featureGraphic: CarouselImage | null;
}

function formatScreenLabel(key: string): string {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function buildCarouselImages(theme: ThemeDetailResponse): CarouselBuildResult {
  const map = theme.preview_urls ?? {};
  const images: CarouselImage[] = [];
  for (const key of SCREEN_KEYS) {
    const url = map[key];
    if (url) images.push({ url, label: formatScreenLabel(key), key });
  }
  // Also include any additional keys not in SCREEN_KEYS (future-proofing)
  // that are not the feature graphic.
  for (const key of Object.keys(map)) {
    if (key === FEATURE_KEY) continue;
    if ((SCREEN_KEYS as readonly string[]).includes(key)) continue;
    images.push({ url: map[key] as string, label: formatScreenLabel(key), key });
  }
  const featureUrl = map[FEATURE_KEY];
  const featureGraphic: CarouselImage | null = featureUrl
    ? { url: featureUrl, label: formatScreenLabel(FEATURE_KEY), key: FEATURE_KEY }
    : null;
  return { images, featureGraphic };
}

/**
 * CR-111 — `/themes/:slug` detail page.
 *
 * Metadata strip (identity + preview provenance) stacked above the
 * ported ImageCarousel which renders the three non-feature screens +
 * the feature graphic. A single-theme regenerate button (SUPER_ADMIN
 * only) fires the same POST /api/theme-previews endpoint used by the
 * batch flow, scoped to this theme's slug.
 */
export function ThemeDetailPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const { me } = useMe();
  const { theme, loading, error, notFound, refetch } = useTheme(slug ?? null);

  const [categories, setCategories] = useState<readonly CategorySummary[]>([]);
  const [activeJob, setActiveJob] = useState<string | null>(null);
  const polling = useJobPolling(activeJob);
  // CR-112 Task 7: per-tile regenerate — which screen (if any) is being
  // confirmed right now? Null means no per-tile dialog is open.
  const [tileRegenKey, setTileRegenKey] = useState<string | null>(null);
  // CR-112 Task 8: which screens failed to load their <img>? Tracked as a
  // Set so fixing a second (or the same) screen toggles the banner.
  const [failedKeys, setFailedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    listActiveCategories(ctrl.signal)
      .then((resp) => {
        if (cancelled) return;
        setCategories(resp.items);
      })
      .catch(() => {
        // Category name is a nice-to-have.
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Refresh detail after a successful regenerate so is_stale and
  // last_generated reflect the new reality.
  useEffect(() => {
    if (polling.stopped && !polling.error && activeJob !== null) {
      refetch();
    }
  }, [polling.stopped, polling.error, activeJob, refetch]);

  const canRegenerate = me?.system_role === "SUPER_ADMIN";
  const jobInFlight = activeJob !== null && !polling.stopped;

  const handleRegenerate = useCallback(async (): Promise<void> => {
    if (!theme) return;
    try {
      const resp = await triggerPreviewGeneration({
        force: true,
        theme_id: theme.slug,
      });
      setActiveJob(resp.job_id);
    } catch {
      // fetchApi surfaces errors via API_ERROR_BUS.
    }
  }, [theme]);

  // CR-112 Task 7 — per-tile regenerate confirm handler. Fires a
  // single-screen POST and clears the dialog; polling + refetch happen
  // via the existing useJobPolling hook + effect below.
  const handleConfirmTileRegen = useCallback(async (): Promise<void> => {
    if (!theme || !tileRegenKey) return;
    const key = tileRegenKey;
    setTileRegenKey(null);
    try {
      const resp = await triggerPreviewGeneration({
        force: true,
        theme_id: theme.slug,
        screen_keys: [key],
      });
      setActiveJob(resp.job_id);
      // On success, clear any earlier failure banner for the same screen.
      setFailedKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } catch {
      // fetchApi surfaces errors via API_ERROR_BUS.
    }
  }, [theme, tileRegenKey]);

  // CR-112 Task 8 — record image-load failures so an amber banner can
  // surface on the failing tile. Scoped per-screen-key to keep other
  // tiles clean.
  const handleTileImageError = useCallback(
    (image: CarouselImage): void => {
      if (!image.key) return;
      setFailedKeys((prev) => {
        if (prev.has(image.key!)) return prev;
        const next = new Set(prev);
        next.add(image.key!);
        return next;
      });
    },
    [],
  );

  // CR-112 Task 7 overlay + Task 8 banner render props — admin-gated so
  // non-admin users never see the per-tile machinery (spec + Gemini
  // Pass 1 HIGH #2: tabbable via focus-within + hover, styled in CSS).
  const renderTileOverlay = useMemo(() => {
    if (!canRegenerate) return undefined;
    return (image: CarouselImage): JSX.Element | null => {
      if (!image.key) return null;
      return (
        <button
          type="button"
          className="theme-detail-tile__overlay-button"
          data-testid={`tile-regen-${image.key}`}
          aria-label={`Regenerate ${image.key}`}
          onClick={(e) => {
            e.stopPropagation();
            setTileRegenKey(image.key!);
          }}
        >
          <RefreshCw size={14} aria-hidden />
          Regenerate
        </button>
      );
    };
  }, [canRegenerate]);

  const renderTileBanner = useMemo(() => {
    if (!canRegenerate) return undefined;
    return (image: CarouselImage): JSX.Element | null => {
      if (!image.key) return null;
      if (!failedKeys.has(image.key)) return null;
      return (
        <div
          className="theme-detail-tile__failure-banner"
          data-testid={`tile-failure-banner-${image.key}`}
          role="status"
        >
          <AlertTriangle size={14} aria-hidden /> Generation failed — regenerate
        </div>
      );
    };
  }, [canRegenerate, failedKeys]);

  const categoryName = theme
    ? categories.find((c) => c.id === theme.category_id)?.name
    : undefined;

  return (
    <section className="theme-detail-page">
      <nav aria-label="Back" className="theme-detail-page__back-nav">
        <Link to="/themes" className="theme-detail-page__back-link">
          <ArrowLeft size={16} aria-hidden /> All themes
        </Link>
      </nav>

      {loading ? (
        <div
          className="theme-detail-page__skeleton skeleton-fade-in"
          aria-busy="true"
          aria-label="Loading theme"
        >
          <div className="skeleton skeleton--title" style={{ width: "40%" }} />
          <div className="skeleton skeleton--text" />
          <div className="theme-detail-page__skeleton-carousel skeleton" />
        </div>
      ) : notFound ? (
        <div className="theme-detail-page__notfound">
          <div className="theme-detail-page__notfound-card">
            <h1>Theme not found</h1>
            <p>
              We couldn't find a theme with the slug{" "}
              <code>{slug ?? "(none)"}</code>.
            </p>
            <Link to="/themes" className="btn btn--secondary">
              Back to themes
            </Link>
          </div>
        </div>
      ) : error ? (
        <div className="error-banner" role="alert">
          <span className="error-banner__icon" aria-hidden>
            <AlertTriangle size={18} />
          </span>
          <div className="error-banner__content">
            <div className="error-banner__title">Failed to load theme</div>
            <div className="error-banner__message">{error.message}</div>
          </div>
        </div>
      ) : theme ? (
        <>
          <ThemeMetadataStrip theme={theme} categoryName={categoryName} />

          {canRegenerate ? (
            <div className="theme-detail-page__actions">
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  void handleRegenerate();
                }}
                disabled={jobInFlight}
              >
                <RefreshCw size={16} aria-hidden />
                Regenerate this theme
              </button>
            </div>
          ) : null}

          <div className="theme-detail-page__carousel-wrapper">
            {(() => {
              const { images, featureGraphic } = buildCarouselImages(theme);
              return (
                <ImageCarousel
                  images={images}
                  featureGraphic={featureGraphic}
                  renderTileOverlay={renderTileOverlay}
                  renderTileBanner={renderTileBanner}
                  onTileImageError={
                    canRegenerate ? handleTileImageError : undefined
                  }
                />
              );
            })()}
          </div>

          <JobProgressPanel
            status={polling.status}
            stopped={polling.stopped}
            error={polling.error}
          />

          {/* CR-112 Task 7 — plain ConfirmDialog for per-tile regenerate
              (no type-to-confirm phrase; single-image regen is cheap).
              The dialog's message names the screen so the operator
              always sees what they're about to redo. */}
          <ConfirmDialog
            open={tileRegenKey !== null}
            title={
              tileRegenKey ? `Regenerate ${tileRegenKey}` : "Regenerate tile"
            }
            message={
              tileRegenKey
                ? `Regenerate the ${tileRegenKey} preview for this theme? ~30 s.`
                : ""
            }
            confirmLabel="Regenerate"
            confirmVariant="primary"
            onConfirm={() => {
              void handleConfirmTileRegen();
            }}
            onCancel={() => setTileRegenKey(null)}
          />
        </>
      ) : null}
    </section>
  );
}
